/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/SearchResultCard.jsx
// CBETA 風格搜尋結果卡片元件 - 支援多片段顯示、收合展開、每筆連結

import { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SourceIcon from '@mui/icons-material/Source';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { isRestricted, RESTRICTED_CHIP_LABEL } from '../../constants/roles';

const SearchResultCard = ({ result, query }) => {
  // CBETA 風格：result 包含 documentId 和 snippets 陣列
  // snippets 由前端 snippetExtractor 提取（Plan B）
  // sourceName 由後端 JOIN sources 表取得
  // [2025-12-19] totalMatches：後端返回的實際出現總數
  const {
    documentId,
    title,
    snippets,
    source,
    sourceName,
    origin,
    created_at,
    tags,
    totalMatches,
    access_level,
  } = result;

  // 展開狀態（預設展開，顯示所有片段）
  const [expanded, setExpanded] = useState(true);

  // DOMPurify 配置：只允許 mark 標籤（搜尋高亮用）
  const sanitizeConfig = useMemo(
    () => ({
      ALLOWED_TAGS: ['mark', 'br'],
      ALLOWED_ATTR: [],
    }),
    []
  );

  /**
   * 淨化 HTML 內容，防止 XSS 攻擊
   * @param {string} html - 原始 HTML
   * @returns {string} 淨化後的 HTML
   */
  const sanitizeHtml = (html) => {
    if (!html) return '';
    return DOMPurify.sanitize(html, sanitizeConfig);
  };

  /**
   * 格式化日期
   */
  const formatDate = (dateString) => {
    if (!dateString) return '(無)';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  /**
   * 處理收合/展開
   */
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  /**
   * 處理開啟單筆連結
   *
   * [Plan B D3 / 2026-07-30] 帶字元位置錨點 `pos`（來源為後端 snippet.matchOffset，
   * 見 snippetExtractor 映射），取代從未被讀取過的 `snippet=<序號>`——序號在文章頁
   * 無從對應（片段序 ≠ 章節內高亮序）。position 為 null 時不帶 pos，文章頁退回
   * 「第一個命中」的行為；position 為 0 是合法值（命中在文首）故用 `!= null` 判斷。
   *
   * @param {Object} snippet - 片段物件（含 index / position）
   * @param {Object} e - 點擊事件
   */
  const handleOpenSnippet = (snippet, e) => {
    e.stopPropagation(); // 防止觸發卡片的其他事件
    const position = snippet?.position;
    const posParam = position != null ? `&pos=${encodeURIComponent(position)}` : '';
    const url = `/document/${documentId}?query=${encodeURIComponent(query)}${posParam}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 決定顯示哪些片段
  const hasMultipleSnippets = snippets && snippets.length > 1;
  // 第一筆永遠顯示，其他片段透過 Collapse 控制
  const firstSnippet = snippets && snippets.length > 0 ? [snippets[0]] : [];
  const remainingSnippets = snippets && snippets.length > 1 ? snippets.slice(1) : [];

  return (
    <Card sx={{ mb: 2 }} elevation={2} data-testid="search-result-card">
      <CardContent>
        {/* 標題和「共 X 筆」標籤 + 展開/收合按鈕 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
          >
            <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 0 }}>
              {title}
            </Typography>
            {isRestricted(access_level) && (
              <Chip
                label={RESTRICTED_CHIP_LABEL}
                size="small"
                color="info"
                data-testid="restricted-chip"
              />
            )}
          </Box>

          {/* 右側：共 X 筆 + 展開/收合按鈕 */}
          {hasMultipleSnippets && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`${totalMatches || snippets.length} 處`}
                size="small"
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{ p: 0.5 }}
                data-testid="expand-snippets-btn"
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          )}
        </Box>

        {/* 元資料（來源、出處、日期、標籤） */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          {/* 來源 */}
          {(sourceName || source) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SourceIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                來源：{sourceName || source}
              </Typography>
            </Box>
          )}

          {/* 出處 */}
          {origin && (
            <Typography variant="caption" color="text.secondary">
              出處：{origin}
            </Typography>
          )}

          {/* 上傳日期 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {formatDate(created_at)}
            </Typography>
          </Box>
        </Box>

        {/* 標籤 */}
        {tags && tags.length > 0 && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 2, flexWrap: 'wrap' }}
          >
            <LocalOfferIcon fontSize="small" color="action" />
            {tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* 第一筆片段（永遠顯示） */}
        {firstSnippet && firstSnippet.length > 0 && (
          <Box>
            {firstSnippet.map((snippet) => {
              // 前端片段提取格式（Plan B）
              const snippetId = snippet.index;
              const snippetText = snippet.snippet;

              return (
                <Box
                  key={snippetId}
                  sx={{
                    mb: 2,
                    position: 'relative',
                    pr: 4, // 為右上角圖示留空間
                    '&:last-child': { mb: 0 },
                  }}
                >
                  {/* 橘色編號（參考 CBETA） - 強制顯示阿拉伯數字 */}
                  <Typography
                    component="span"
                    data-testid={`snippet-id-${snippetId}`}
                    sx={{
                      color: '#ff6f00',
                      fontWeight: 'bold',
                      mr: 1,
                      fontSize: '1.1rem',
                    }}
                  >
                    {String(snippetId)}
                  </Typography>

                  {/* 片段內容（包含前端高亮，已淨化防止 XSS） */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                    key={`snippet-text-${documentId}-${snippetId}`}
                    sx={{
                      lineHeight: 1.8,
                      '& mark': {
                        backgroundColor: '#fff9c4',
                        color: '#000',
                        padding: '2px 4px',
                        border: '1px solid #fbc02d',
                        borderRadius: '2px',
                        fontWeight: 'bold',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(snippetText) }}
                  />

                  {/* 右上角「開啟連結」圖示 */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenSnippet(snippet, e)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      color: 'action.active',
                      '&:hover': { color: 'primary.main' },
                    }}
                    title={`開啟第 ${snippetId} 筆`}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}

        {/* 其他片段（可收合） */}
        {hasMultipleSnippets && (
          <Collapse in={expanded} timeout="auto" unmountOnExit key={`collapse-${documentId}`}>
            <Box key={`snippets-box-${documentId}`}>
              {remainingSnippets.map((snippet) => {
                // 前端片段提取格式（Plan B）
                const snippetId = snippet.index;
                const snippetText = snippet.snippet;

                return (
                  <Box
                    key={snippetId}
                    sx={{
                      mb: 2,
                      position: 'relative',
                      pr: 4,
                      '&:last-child': { mb: 0 },
                    }}
                  >
                    {/* 橘色編號 - 強制顯示阿拉伯數字 */}
                    <Typography
                      component="span"
                      data-testid={`snippet-id-${snippetId}`}
                      sx={{
                        color: '#ff6f00',
                        fontWeight: 'bold',
                        mr: 1,
                        fontSize: '1.1rem',
                      }}
                    >
                      {String(snippetId)}
                    </Typography>

                    {/* 片段內容（包含前端高亮，已淨化防止 XSS） */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                      key={`snippet-text-${documentId}-${snippetId}`}
                      sx={{
                        lineHeight: 1.8,
                        '& mark': {
                          backgroundColor: '#fff9c4',
                          color: '#000',
                          padding: '2px 4px',
                          border: '1px solid #fbc02d',
                          borderRadius: '2px',
                          fontWeight: 'bold',
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(snippetText) }}
                    />

                    {/* 右上角「開啟連結」圖示 */}
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenSnippet(snippet, e)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        color: 'action.active',
                        '&:hover': { color: 'primary.main' },
                      }}
                      title={`開啟第 ${snippetId} 筆`}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        )}
      </CardContent>

      <CardActions>
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          component="a"
          href={`/document/${documentId}?query=${encodeURIComponent(query)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          查看完整內容
        </Button>
      </CardActions>
    </Card>
  );
};

export default SearchResultCard;
